import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { MapPin, Receipt, ListTodo } from "lucide-react";

// Native ERP port of the standalone Sales Reporter app's "Home" tab.
// Greeting + role badge, today's stat tiles (reusing the existing
// my-summary?period=daily endpoint from Phase 4 — no new backend route
// needed for this page), an upcoming-plans preview, and quick actions
// that jump to the Visits/Expenses/Plans pages built in earlier phases.
//
// Phase 6 note: every user sees "Sales Report" in the sidebar now, even
// before opting in, so Home checks `opted_in` — a field Phase 1's
// GET /me has always returned — and shows a friendly notice instead of
// an empty (but not actually broken) zero-stat dashboard. No backend
// route or logic changes; this reads data the API already exposed.
const money = (n) => `₹ ${Number(n || 0).toLocaleString()}`;

function StatTile({ label, value }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4">
      <div className="text-xs uppercase tracking-[0.12em] text-slate-500">{label}</div>
      <div className="text-xl font-bold text-slate-900 mt-1 mono">{value}</div>
    </div>
  );
}

export default function SalesHomePage() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [summary, setSummary] = useState(null);
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    api.get("/sales-report/me").then(({ data }) => setProfile(data)).catch(() => {});
    api.get("/sales-report/my-summary", { params: { period: "daily" } }).then(({ data }) => setSummary(data)).catch(() => {});
    api.get("/sales-report/plans", { params: { status: "open" } }).then(({ data }) => setPlans(data.slice(0, 5))).catch(() => {});
  }, []);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  const notOptedIn = profile != null && profile.opted_in === false;

  return (
    <div className="space-y-6" data-testid="sales-home-page">
      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Sales Report</div>
        <h1 className="text-3xl sm:text-4xl tracking-tight font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>
          {greeting}{profile?.name ? `, ${profile.name}` : ""}
        </h1>
        {profile?.effective_sales_role && (
          <span className="inline-block mt-2 text-xs uppercase tracking-[0.1em] font-semibold bg-blue-50 text-blue-700 px-2.5 py-1 rounded-full" data-testid="role-badge">
            {profile.sales_role_label || profile.effective_sales_role}
          </span>
        )}
      </div>

      {notOptedIn ? (
        <div className="bg-white border border-slate-200 rounded-lg p-6 text-center max-w-lg" data-testid="not-opted-in-notice">
          <p className="text-sm text-slate-600">
            You haven't been added to the Sales Report module yet — contact your GM.
          </p>
        </div>
      ) : (
      <>
      <div className="grid grid-cols-3 gap-3">
        <Button className="h-auto py-4 flex-col gap-1.5 bg-blue-600 hover:bg-blue-700" onClick={() => navigate("/sales-report/visits")} data-testid="quick-log-visit">
          <MapPin className="w-5 h-5" />
          <span>Log Visit</span>
        </Button>
        <Button className="h-auto py-4 flex-col gap-1.5 bg-blue-600 hover:bg-blue-700" onClick={() => navigate("/sales-report/expenses")} data-testid="quick-log-expense">
          <Receipt className="w-5 h-5" />
          <span>Log Expense</span>
        </Button>
        <Button className="h-auto py-4 flex-col gap-1.5 bg-blue-600 hover:bg-blue-700" onClick={() => navigate("/sales-report/plans")} data-testid="quick-add-plan">
          <ListTodo className="w-5 h-5" />
          <span>Add Plan</span>
        </Button>
      </div>

      {summary && (
        <div>
          <div className="text-xs uppercase tracking-[0.14em] text-slate-500 mb-2">Today</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatTile label="Visits" value={summary.visits.total_visits} />
            <StatTile label="Quotations Sent" value={summary.visits.quotations_sent} />
            <StatTile label="Orders Received" value={summary.visits.orders_received} />
            <StatTile label="Order Value" value={money(summary.visits.order_value)} />
            <StatTile label="KM Travelled" value={Number(summary.expenses.total_km || 0)} />
            <StatTile label="Expenses" value={money(summary.expenses.total_expenses)} />
          </div>
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs uppercase tracking-[0.14em] text-slate-500">Upcoming Plans</div>
          <Button variant="ghost" size="sm" onClick={() => navigate("/sales-report/plans")} data-testid="view-all-plans">View all</Button>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100">
          {plans.map((p) => (
            <div key={p.id} className="p-3 flex items-center justify-between" data-testid={`home-plan-${p.id}`}>
              <div>
                <div className="text-sm font-medium text-slate-800">{p.purpose}</div>
                {p.contact_name && <div className="text-xs text-slate-500">{p.contact_name}</div>}
              </div>
              {p.plan_date && <div className="text-xs text-slate-400">{p.plan_date}</div>}
            </div>
          ))}
          {plans.length === 0 && (
            <div className="p-6 text-center text-slate-400 text-sm">No open plans.</div>
          )}
        </div>
      </div>
      </>
      )}
    </div>
  );
}
